/**
 * LabelingLogin - Email/password login form
 *
 * Provides authentication interface for labelers
 * Requirement: ES-REQ-057 (Authentication Panel), ES-REQ-080-082
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { useLabeling } from '@/context/labelingContext';

const Container = styled.div`
  max-width: 400px;
  margin: 100px auto;
  padding: 32px;
  background-color: var(--bg-card);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h2`
  margin: 0 0 24px 0;
  font-size: 24px;
  font-weight: var(--font-weight-semibold);
  color: var(--text-dark);
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  color: #555;
`;

const Input = styled.input`
  padding: 10px 12px;
  font-size: var(--font-size-base);
  border: 1px solid #ddd;
  border-radius: 4px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent-feedback);
  }

  &:disabled {
    background-color: var(--bg-panel);
    cursor: not-allowed;
  }
`;

const Button = styled.button`
  padding: 12px;
  background-color: var(--accent-feedback);
  color: var(--text-inverse);
  border: none;
  border-radius: 4px;
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: background-color 0.2s;
  margin-top: 8px;

  &:hover:not(:disabled) {
    background-color: #1976d2;
  }

  &:active:not(:disabled) {
    background-color: #1565c0;
  }

  &:disabled {
    background-color: #bbb;
    cursor: not-allowed;
  }
`;

const HelpText = styled.p`
  margin: 16px 0 0 0;
  font-size: 12px;
  color: var(--text-muted);
  text-align: center;
  line-height: 1.5;
`;

export const LabelingLogin: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { state, login } = useLabeling();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      return;
    }

    try {
      await login(email, password);
    } catch (error) {
      // Error is handled by context and displayed in StatusFeedback
      console.error('Login failed:', error);
    }
  };

  return (
    <Container>
      <Title>Feedback Labeling</Title>
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="expert@example.com"
            disabled={state.isLoading}
            required
            autoComplete="email"
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
            disabled={state.isLoading}
            required
            autoComplete="current-password"
          />
        </FormGroup>
        <Button type="submit" disabled={state.isLoading || !email || !password}>
          {state.isLoading ? 'Signing in...' : 'Sign In'}
        </Button>
      </Form>
      <HelpText>
        This is a staging-only feature for labeling user feedback.
      </HelpText>
    </Container>
  );
};

export default LabelingLogin;
